import keycloak from 'src/app/keycloak-init';
import {
  getCurrentUserEmail,
  getCurrentUsername,
  getManufacturingBatchActorEmployeeId,
  getManufacturingBatchDisplayName,
  resolveEmployeeIdByEmail,
} from 'src/app/utils/manufacturing-batch-actor.util';

const EMPLOYEE_ID_CLAIMS = [
  'sessionId',
  'employeeId',
  'employee_id',
  'Id',
  'id',
  'userId',
  'user_id',
] as const;

function parsePositiveInt(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && raw > 0 ? Math.trunc(raw) : undefined;
  }
  const n = parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function getTokenClaims(): Record<string, unknown> {
  if (keycloak.tokenParsed) {
    return keycloak.tokenParsed as Record<string, unknown>;
  }
  const token = keycloak.token;
  if (!token) return {};
  try {
    const payload = token.split('.')[1];
    if (!payload) return {};
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(normalized)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function readClaimValue(claims: Record<string, unknown>, key: string): unknown {
  if (claims[key] != null && claims[key] !== '') return claims[key];
  const lower = key.toLowerCase();
  const match = Object.keys(claims).find((k) => k.toLowerCase() === lower);
  return match ? claims[match] : undefined;
}

/** Lee ID del empleado desde el access token (prioriza sessionId). */
export function readAsignacionesEmployeeIdFromToken(): number | undefined {
  const claims = getTokenClaims();
  for (const key of EMPLOYEE_ID_CLAIMS) {
    const id = parsePositiveInt(readClaimValue(claims, key));
    if (id) return id;
  }
  return undefined;
}

function resolveEmployeeIdByUsername(
  employees: Array<{ id: number; email?: string | null }>,
): number | undefined {
  const username = getCurrentUsername().toLowerCase();
  if (!username) return undefined;

  return employees.find((employee) => {
    const email = employee.email?.trim().toLowerCase();
    if (!email) return false;
    const localPart = email.split('@')[0];
    return email === username || localPart === username;
  })?.id;
}

function resolveEmployeeIdFromList(
  employees: Array<{ id: number; email?: string | null }>,
): number | undefined {
  return resolveEmployeeIdByEmail(employees) ?? resolveEmployeeIdByUsername(employees);
}

/**
 * ID del actor para asignaciones.
 * Orden: token SSO → util de lotes (sin tocar) → localStorage → email/username.
 */
export function getAsignacionesActorEmployeeId(
  employees?: Array<{ id: number; email?: string | null }>,
): number | undefined {
  if (keycloak.authenticated) {
    const fromToken = readAsignacionesEmployeeIdFromToken();
    if (fromToken) return fromToken;
  }

  const fromBatchUtil = getManufacturingBatchActorEmployeeId();
  if (fromBatchUtil) return fromBatchUtil;

  const fromStorage =
    parsePositiveInt(localStorage.getItem('employeeId')) ??
    parsePositiveInt(localStorage.getItem('sessionId'));
  if (fromStorage) return fromStorage;

  if (!employees?.length) return undefined;
  return resolveEmployeeIdFromList(employees);
}

/** Persiste en localStorage el ID resuelto desde Keycloak. */
export function syncAsignacionesActorSession(): void {
  if (!keycloak.authenticated) return;

  const employeeId = getAsignacionesActorEmployeeId();
  if (employeeId == null) return;

  localStorage.setItem('employeeId', String(employeeId));
  localStorage.setItem('sessionId', String(employeeId));

  const email = getCurrentUserEmail();
  if (email) localStorage.setItem('email', email);

  const tp = getTokenClaims();
  const givenName = (tp['given_name'] as string) || '';
  const familyName = (tp['family_name'] as string) || '';
  const fullName = getManufacturingBatchDisplayName();
  if (givenName) localStorage.setItem('name', givenName);
  if (familyName) localStorage.setItem('surname', familyName);
  else if (fullName && !givenName) {
    const [first, ...rest] = fullName.split(/\s+/);
    if (first) localStorage.setItem('name', first);
    if (rest.length) localStorage.setItem('surname', rest.join(' '));
  }
}
